/**
 * Was der Offline-Ausgang mit einer Serverantwort tut. Die Regel steht hier und nicht in
 * der Oberfläche, weil sie über den Verbleib einer Meldung entscheidet und getestet sein muss.
 *
 * - `ok`: angenommen
 * - `retry`: Der Server kam nicht dazu, die Meldung zu beurteilen. Später erneut.
 * - `hold`: Liegt an der Sitzung oder am Netz des Geräts, nicht an der Meldung.
 * - `reject`: Der Server lehnt die Meldung ab, nochmal senden ändert von selbst nichts.
 */
export type OutboxVerdict = 'ok' | 'retry' | 'hold' | 'reject';

const HOLD_CODES: readonly (string | undefined)[] = ['password_change_required', 'quick_network'];
const RETRY_STATUS: readonly number[] = [408, 425, 429];

export function outboxVerdict(status: number, code?: string): OutboxVerdict {
  if (status >= 200 && status < 300) return 'ok';
  if (status === 401 || (status === 403 && HOLD_CODES.includes(code))) return 'hold';
  if (status < 400 || status >= 500 || RETRY_STATUS.includes(status)) return 'retry';
  // Unsere 4xx tragen alle einen Code. Ohne ihn antwortet ein Proxy, etwa beim Container-Neustart,
  // und das trifft sonst den ganzen Ausgang. Einzige Ausnahme ist die CSRF-Sperre von Hono: 403 mit reinem Text.
  return code || status === 403 ? 'reject' : 'retry';
}

/** Frist auf dem Gerät: Es kann geteilt sein, und das Löschkonzept des Servers erreicht diesen Speicher nicht. */
export const OUTBOX_MAX_AGE_HOURS = 72;

export function outboxExpired(createdAt: string, now = Date.now()): boolean {
  return !(now - Date.parse(createdAt) < OUTBOX_MAX_AGE_HOURS * 3_600_000);
}

/** Abstand der Versuche ohne äußeren Anlass: Im WLAN ohne Uplink meldet der Browser keine Rückkehr des Netzes. */
export const OUTBOX_RETRY_MIN_MS = 30_000;
export const OUTBOX_RETRY_MAX_MS = 5 * 60_000;

export function outboxNextDelay(delayMs: number): number {
  return Math.min(delayMs * 2, OUTBOX_RETRY_MAX_MS);
}

type OutboxEntry = { kind: string; ownerUserId?: string | undefined };

/**
 * Worauf sich ein Versuch ohne äußeren Anlass lohnt. Abgelehnte warten auf den Melder. Eine gehaltene
 * `web`-Meldung wartet auf eine Anmeldung, die beim Start ohnehin sendet. Jede Anfrage verlängert die
 * Sitzung, ein Timer hielte also nur die Sitzung der Person wach, die gerade am Gerät angemeldet ist.
 */
export function outboxRetries(entry: OutboxEntry & { state?: string | undefined }): boolean {
  if (entry.state === 'rejected' || entry.state === 'expired') return false;
  return !(entry.kind === 'web' && entry.state === 'held');
}

/**
 * Bei `web` setzt der Server den Melder aus der Sitzung, die Meldung gehört also der Person,
 * die sie gespeichert hat. Altbestand ohne Besitzer geht nur auf ausdrücklichen Klick raus.
 * Schnellmeldung und Kiosk tragen den Namen im Text und hängen an keiner Sitzung.
 */
export function outboxMaySend(entry: OutboxEntry, account: { id: string; mustChangePassword: boolean } | null): boolean {
  if (entry.kind !== 'web') return true;
  return account !== null && !account.mustChangePassword && entry.ownerUserId === account.id;
}

export function outboxVisibleTo(entry: OutboxEntry, userId: string): boolean {
  return entry.kind !== 'web' || entry.ownerUserId === undefined || entry.ownerUserId === userId;
}
